import { Referrer } from '@api/user/types';
import { API } from '@providers/ApiInterceptorProvider/useApiInterceptor';
import { ExistingFirmResponse, FCAOrganisationDetails, LinkBrokerToFirmResult } from './firm.types';

interface ReferrerWithLinksDto {
  referrer: { id: string };
  links: { firm: string };
}

const trimFca = (fca: string) => fca.trim();

function parseFirmIdFromLink(firmLink: string): string | null {
  if (!firmLink) {
    return null;
  }
  const segments = firmLink.split('/').filter(Boolean);
  const firmsIndex = segments.lastIndexOf('firms');
  if (firmsIndex === -1 || firmsIndex === segments.length - 1) {
    return null;
  }
  return segments[firmsIndex + 1] ?? null;
}

export const firmApi = {
  getFirmByFca: async (fca: string): Promise<ExistingFirmResponse | null> => {
    const { data } = await API.get<ExistingFirmResponse[]>(`/monolith/referrers/firms`, {
      params: { fca: trimFca(fca) },
    });
    return data.at(0) ?? null;
  },

  getFirmDetails: async (fca: string): Promise<FCAOrganisationDetails> => {
    const res = await API.get<FCAOrganisationDetails>(`/monolith/referrers/${trimFca(fca)}/firm-details`);
    return res.data;
  },

  linkBrokerToExistingFirm: async (firmId: string): Promise<LinkBrokerToFirmResult> => {
    const { data } = await API.post<LinkBrokerToFirmResult>(`/monolith/referrers/firms/${firmId}/brokers`);
    return data;
  },

  linkFirmToMortgageClub: async (firmId: string, clubId: string) => {
    await API.patch(`/monolith/referrers/firms/${firmId}/linkToClub/${clubId}`);
  },
  linkFirmToNetwork: async (firmId: string, networkId: string) => {
    await API.patch(`/monolith/referrers/firms/${firmId}/linkToNetwork/${networkId}`);
  },

  getFirmById: async (firmId: string): Promise<ExistingFirmResponse> => {
    const { data } = await API.get<ExistingFirmResponse>(`/monolith/referrers/firms/${firmId}`);
    return data;
  },

  getBrokerReferrerByUserId: async (userId: number): Promise<Referrer> => {
    const { data } = await API.get<Referrer>(`/monolith/referrers?userId=${userId}`);
    return data;
  },

  getIntroducerById: async (referrerId: string): Promise<Referrer> => {
    const { data } = await API.get<Referrer>(`/monolith/referrers/introducers/${referrerId}`);
    return data;
  },

  getBrokerFirm: async (userId: number): Promise<ExistingFirmResponse | null> => {
    const referrer = await firmApi.getBrokerReferrerByUserId(userId);
    const { data: brokerWithLinks } = await API.get<ReferrerWithLinksDto>(
      `/monolith/referrers/brokers/${referrer.id}`,
      { params: { includeFirm: true } },
    );
    const firmId = parseFirmIdFromLink(brokerWithLinks.links.firm);
    if (!firmId) {
      return null;
    }
    return firmApi.getFirmById(firmId);
  },
};
